import React, {useEffect, useState} from "react";
import {useUserAuthContext} from "../../context/UserAuthContext";
import {useGetUserWallet, useGetWalletKPI} from "../../services/auths";
import {useSearchParams} from "react-router-dom";
import {toast} from "react-hot-toast";
import moment from "moment/moment";
import {CURRENCY, CURRENCY_STRING} from "../../constants";
import {Next, TopUpArrow} from "../../assets/svg-code";
import CustomTable from "../../comps/CustomTable";
import { getWalletPageNumber } from "../../utils/wallet";
// built-in modal implementations
import TopUpInfoModal from "./topUpInfo";
import TopUpModal from "./topUp";
import AdminTopUpModal from "./adminTopUp";

interface WalletCoreProps {
    merchantId?: string;
    // preferred: pass a component to render the top-up modal (composition)
    TopUpComponent?: React.ComponentType<any>;
    // fallback: choose one of the built-in variants
    topUpVariant?: "user" | "admin" | "info";
}

const formatAmount = (value: any, currency?: string) => {
    const num = Number(value) || 0;
    const isUnit = String((currency ?? CURRENCY) || "").toLowerCase() === "unit";
    const digits = isUnit ? 0 : 2;
    return num.toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const WalletCore: React.FC<WalletCoreProps> = ({merchantId, TopUpComponent, topUpVariant = "info"}) => {
    const [loginModal, setLoginModal] = useState<boolean>(false);
    const {KYC} = useUserAuthContext();

    const {loading, wallets, pageInfo, setPageInfo, handlegetWallet} = useGetUserWallet(merchantId);
    const {handleGetWalletKPI, KPI} = useGetWalletKPI(merchantId);

    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get("status") === "SUCCESS" && searchParams.get("transactionRef")) {
            toast.success(`Top up successful`, {duration: 3000});
        }
    }, [searchParams]);

    // resolve which top-up modal to render
    const ResolvedTopUp = TopUpComponent
        ?? (topUpVariant === "admin" ? AdminTopUpModal : topUpVariant === "user" ? TopUpModal : TopUpInfoModal);

    // pass common props; also forward merchantId so admin modal can use it
    const topUpProps = {loginModal, setLoginModal, handlegetWallet, handleGetWalletKPI, merchantId};

    // ... cards, columns, sampleCustomers same as before (omitted for brevity) ...
    // keep rest of WalletCore UI unchanged, just render ResolvedTopUp where TopUpInfoModal used earlier
    const isTopUpDisabled = KYC?.status !== "APPROVED" && topUpVariant !== "admin";

    const sampleCustomers: any = {
        total: pageInfo?.totalElements ?? wallets?.length,
        data: wallets?.map((wallet: any) => ({
            key: wallet?.id,
            sn: "S/N",
            date: moment(wallet?.createdAt).format("MMM DD, YYYY h:mm A"),
            balance: formatAmount(
                wallet?.creditAmount > wallet?.debitAmount ? wallet?.creditAmount : wallet?.debitAmount
            ),
            type: wallet?.creditAmount > wallet?.debitAmount ? "Credit" : "Debit",
        })),
    };

    const cards = [
        {
            title: "Wallet Balance",
            value:
                typeof KPI?.balance === "number"
                    ? formatAmount(KPI?.balance)
                    : formatAmount(KPI?.balance?.balance),
        },
        {
            title: "Amount Funded",
            value: formatAmount(KPI?.amountFund),
        },
        {
            title: "Amount Spent",
            value: formatAmount(KPI?.amountSpent?.[0]?.total),
        },
    ];

    const columns = [
        {
            title: "S.N",
            index: "sn",
            dataIndex: "s/n",
            render: (_text: string, _record: {}, index: number) => <div>{index + 1}</div>,
        },
        {
            title: <div>Date</div>,
            render: (record: any) => <div>{record?.date}</div>,
        },
        {
            title: <div>Amount ( {CURRENCY_STRING})</div>,
            render: (record: any) => <div>{record?.balance}</div>,
        },
        {
            title: "Transaction Type",
            render: (record: any) => {
                return (
                    <div>
            <span
                className={"px-5 py-2 text-xs"}
                style={{
                    backgroundColor: record?.type === "Credit" ? "#c5f4c5" : "#ffd6d6",
                    color: record?.type === "Credit" ? "#0a990a" : "#ab0909",
                }}
            >
              {record?.type}
            </span>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <div className="grid gap-8" style={{gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))"}}>
                {cards.map((card) => (
                    <div className="w-full bg-[#FAFDFF] rounded-lg p-5 border border-[#D5D7D8]" key={card.title}>
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-[20px] inter">{card.title}</h3>
                        </div>
                        <h1 className="text-4xl   text-[56px] inter">
                            {CURRENCY_STRING}
                            {card.value}
                        </h1>
                        {card.title === "Wallet Balance" ? (
                            <button
                                className={`
    ${isTopUpDisabled ? "bg-[#ccdce9]" : "bg-[#184E77]"}
    ${isTopUpDisabled ? "cursor-not-allowed" : "cursor-pointer"}
    p-2 px-3 rounded-lg text-white w-fit flex mr-0 ml-auto`}
                                onClick={() => setLoginModal(true)}
                                disabled={isTopUpDisabled}
                            >
                                Top Up <TopUpArrow/>
                            </button>
                        ) : null}
                    </div>
                ))}
            </div>
            <div className="flex justify-center p-3 items-center gap-x-3 text-[20px]">
                This month Summary
                <Next/>
            </div>
            <div className="mt-10 w-full">
                <CustomTable
                    tableName="Customers"
                    dataSource={sampleCustomers.data}
                    loading={loading}
                    columns={columns}
                pagination={{
                    pageSize: pageInfo.limit,
                    current: getWalletPageNumber(pageInfo.number),
                    total: sampleCustomers.total,
                    onChange: (page: number, pageSize: number) => {
                        setPageInfo({
                            ...pageInfo,
                            number: page - 1,
                            limit: pageSize,
                        });
                    },
                }}
            />
            </div>

            {/* render chosen top-up modal */}
            <ResolvedTopUp {...topUpProps} />
        </>
    );
};

export default WalletCore;
